import { createController, emptyOptionSearch, optionQueryAction, todayIso } from '@eo/shared';

import {
  analyseDay,
  createEntry,
  fetchAnalysedDates,
  deleteEntry,
  dismissSuggestion,
  fetchClientTasks,
  fetchDayTimeline,
  fetchTaskTypes,
  fetchClients,
  searchTaskTypes,
  setEntryTaskType,
  updateEntry,
} from './api/api';
import type {
  CreateEntryBody,
  DismissalBody,
  SetTaskTypeBody,
  TimelineRow,
  UpdateEntryBody,
} from './api/types';
import { VIEW_AS_PARAM } from './constants';
import { applyConfirmation, byStartTs, isEntryOnly, manualEntryRow, minutesOf } from './helpers';
import type { SetupState, TimeTrackingState } from './types';

// Wide enough that ordinary browsing stays marked without re-fetching as the
// picker's month changes.
const MARKED_WINDOW_DAYS = 45;

function shiftIso(dateIso: string, days: number): string {
  const [y, m, d] = dateIso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

const emptyOptions = (): TimeTrackingState['options'] => ({
  clients: emptyOptionSearch(),
  taskTypes: emptyOptionSearch(),
  tasks: emptyOptionSearch(),
});

const initialState: TimeTrackingState = {
  date: todayIso(),
  analysedAt: null,
  analysedDates: [],
  setupState: null,
  rows: [],
  gaps: [],
  loggedMinutes: 0,
  unconfirmedMinutes: 0,
  googleConnected: false,
  clickupConnected: false,
  slackConnected: false,
  calendarStale: false,
  fromSnapshot: false,
  options: emptyOptions(),
  taskTypes: [],
  viewAs: null,
};

// The wall is for a CFO with nothing wired up at all. One connected source is
// a day worth drawing, and the sources that are missing say so in place.
function setupStateFor(connected: boolean[]): SetupState {
  return connected.some(Boolean) ? null : 'noSources';
}

function recount(state: TimeTrackingState, rows: TimelineRow[]): TimeTrackingState {
  return {
    ...state,
    rows,
    loggedMinutes: minutesOf(rows, true),
    unconfirmedMinutes: minutesOf(rows, false),
  };
}

export const timeTrackingController = createController<TimeTrackingState>({
  name: 'timeTracking',
  state: initialState,
  fetchData: async ({ queryParams }) => {
    const params = (queryParams ?? {}) as Record<string, string | undefined>;
    const date = params.date && /^\d{4}-\d{2}-\d{2}$/.test(params.date) ? params.date : todayIso();
    const viewAs = params[VIEW_AS_PARAM] || null;

    const taskTypesPromise = fetchTaskTypes().catch(() => ({ taskTypes: [] }));
    const analysedPromise = fetchAnalysedDates(
      shiftIso(date, -MARKED_WINDOW_DAYS),
      shiftIso(date, MARKED_WINDOW_DAYS),
      viewAs,
    ).catch(() => ({ dates: [] }));

    const day = await fetchDayTimeline(date, viewAs);

    const [analysed, taskTypes] = await Promise.all([analysedPromise, taskTypesPromise]);
    return {
      date,
      analysedAt: day.analysedAt,
      analysedDates: analysed.dates,
      setupState: setupStateFor([day.clickupConnected, day.googleConnected, day.slackConnected]),
      rows: day.rows,
      gaps: day.gaps,
      loggedMinutes: day.loggedMinutes,
      unconfirmedMinutes: day.unconfirmedMinutes,
      googleConnected: day.googleConnected,
      clickupConnected: day.clickupConnected,
      slackConnected: day.slackConnected,
      calendarStale: day.calendarStale,
      fromSnapshot: day.fromSnapshot,
      options: emptyOptions(),
      taskTypes: taskTypes.taskTypes,
      viewAs,
    };
  },
  asyncActions: {
    loadAnalysedRange: {
      action: async (payload) => {
        const { from, to, userEmail } = payload as {
          from: string;
          to: string;
          userEmail: string | null;
        };
        return fetchAnalysedDates(from, to, userEmail);
      },
      handlers: {
        onSuccess: (state, { action }) => {
          const { dates } = (action as { payload: { dates: string[] } }).payload;
          // Merged, not replaced: browsing to another month must not unmark
          // the month already on screen.
          return { ...state, analysedDates: [...new Set([...state.analysedDates, ...dates])] };
        },
      },
    },
    analyseDay: {
      action: async (payload) => {
        const { date, refresh, userEmail } = payload as {
          date: string;
          refresh: boolean;
          userEmail: string | null;
        };
        return analyseDay(date, refresh, userEmail);
      },
      handlers: {
        onSuccess: (state, { action }) => {
          const day = (action as { payload: Awaited<ReturnType<typeof analyseDay>> }).payload;
          return {
            ...state,
            analysedAt: day.analysedAt,
            analysedDates: state.analysedDates.includes(state.date)
              ? state.analysedDates
              : [...state.analysedDates, state.date],
            rows: day.rows,
            gaps: day.gaps,
            loggedMinutes: day.loggedMinutes,
            unconfirmedMinutes: day.unconfirmedMinutes,
            calendarStale: day.calendarStale,
            fromSnapshot: day.fromSnapshot,
            googleConnected: day.googleConnected,
          };
        },
      },
    },
    logEntry: {
      action: async (payload) => {
        const { body, origin, rowId } = payload as {
          body: CreateEntryBody;
          origin?: { kind: 'task' | 'calendar' | 'slack'; ref: string };
          rowId?: string;
        };
        const res = await createEntry({ ...body, origin });
        return { body, rowId, entryId: res.entryId, startTs: res.startTs };
      },
      // Logging reaches ClickUp, which takes seconds. A block the CFO drew has
      // to stand on the day for those seconds rather than disappear until the
      // round trip answers — the gesture already said what it meant.
      onPending: (state, params) => {
        const p = params as { body: CreateEntryBody; tempId?: string } | undefined;
        if (!p?.tempId || p.body.startTs === undefined) return state;
        return recount(
          state,
          [...state.rows, manualEntryRow(p.tempId, p.body, p.body.startTs, null)].sort(byStartTs),
        );
      },
      handlers: {
        onSuccess: (state, { action, params }) => {
          const { body, rowId, entryId, startTs } = (
            action as {
              payload: {
                body: CreateEntryBody;
                rowId?: string;
                entryId: string | null;
                startTs: number;
              };
            }
          ).payload;

          if (rowId) return recount(state, applyConfirmation(state.rows, rowId, entryId, startTs));

          const tempId = (params as { tempId?: string } | undefined)?.tempId;
          const manual = manualEntryRow(`entry:${entryId ?? startTs}`, body, startTs, entryId);
          const rows = tempId
            ? state.rows.map((r) => (r.id === tempId ? manual : r))
            : [...state.rows, manual];
          return recount(state, [...rows].sort(byStartTs));
        },
        // The entry never landed, so neither does the block that promised it.
        onError: (state, { params }) => {
          const tempId = (params as { tempId?: string } | undefined)?.tempId;
          if (!tempId) return state;
          return recount(
            state,
            state.rows.filter((r) => r.id !== tempId),
          );
        },
      },
    },
    dismiss: {
      action: async (payload) => {
        const { body, rowId } = payload as { body: DismissalBody; rowId: string };
        await dismissSuggestion(body);
        return { rowId };
      },
      handlers: {
        onSuccess: (state, { action }) => {
          const { rowId } = (action as { payload: { rowId: string } }).payload;
          return recount(
            state,
            state.rows.filter((r) => r.id !== rowId),
          );
        },
      },
    },
    updateEntry: {
      action: async (payload) => {
        const { entryId, patch, rowId } = payload as {
          entryId: string;
          patch: UpdateEntryBody;
          rowId: string;
        };
        await updateEntry(entryId, patch);
        return { rowId, patch };
      },
      handlers: {
        onSuccess: (state, { action }) => {
          const { rowId, patch } = (
            action as { payload: { rowId: string; patch: UpdateEntryBody } }
          ).payload;
          return recount(
            state,
            state.rows
              .map((r) =>
                r.id === rowId
                  ? {
                      ...r,
                      startTs: patch.startTs ?? r.startTs,
                      durationMinutes: patch.durationMinutes ?? r.durationMinutes,
                      variable: patch.variable ?? r.variable,
                      // Only a hand-made entry keeps its name in its own
                      // description; everything else is named by its task.
                      title: r.kind === 'manual' && patch.note !== undefined ? patch.note : r.title,
                    }
                  : r,
              )
              .sort(byStartTs),
          );
        },
      },
    },
    setTaskType: {
      action: async (payload) => {
        const { entryId, body, rowId } = payload as {
          entryId: string;
          body: SetTaskTypeBody;
          rowId: string;
        };
        await setEntryTaskType(entryId, body);
        return { rowId, taskTypeId: body.taskTypeId };
      },
      handlers: {
        onSuccess: (state, { action }) => {
          const { rowId, taskTypeId } = (
            action as { payload: { rowId: string; taskTypeId: string | null } }
          ).payload;
          return {
            ...state,
            rows: state.rows.map((r) => (r.id === rowId ? { ...r, taskTypeId } : r)),
          };
        },
      },
    },
    deleteEntry: {
      action: async (payload) => {
        const { entryId, rowId } = payload as { entryId: string; rowId: string };
        await deleteEntry(entryId);
        return { rowId };
      },
      handlers: {
        onSuccess: (state, { action }) => {
          const { rowId } = (action as { payload: { rowId: string } }).payload;
          // A suggestion that was confirmed goes back to being a suggestion; a
          // row that was only ever its entry leaves with it.
          return recount(
            state,
            state.rows.flatMap((r) => {
              if (r.id !== rowId) return [r];
              if (isEntryOnly(r)) return [];
              return [{ ...r, confirmed: false, entryId: null }];
            }),
          );
        },
      },
    },
    queryOptions: optionQueryAction<TimeTrackingState>({
      clients: async (params) => {
        const { clients, pagination } = await fetchClients(params);
        return { items: clients, pagination };
      },
      taskTypes: async (params) => {
        const { taskTypes, pagination } = await searchTaskTypes(params);
        return { items: taskTypes, pagination };
      },
      tasks: async ({ filters, ...params }) => {
        const { tasks, pagination } = await fetchClientTasks(String(filters?.clientId), params);
        return { items: tasks, pagination };
      },
    }),
  },
});
